import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from '@reduxjs/toolkit';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { setUserToken } from '../../../redux/slices/appInfo/slice';
import { store as persistentStore } from '../../../store/Store';
import { clearLoginAsCode, peekLoginAsCode } from '../loginAsHandoff';
import { exchangeLoginAsCode } from './exchangeLoginAsCode';
import { useLoginAsAnalyticsPause, useLoginAsEnded } from './loginAsSession';
import { LoginAsBanner } from './LoginAsBanner';
import { LoginAsNotice } from './LoginAsNotice';

type Phase = 'exchanging' | 'ready' | 'refused';

/**
 * Replaces the session of this browser with the session of the user. This is the storage path of
 * a normal sign-in (`useAuthTokenSuccess`): the persistent `userToken`, then the Redux action.
 */
async function applyLoginAsToken(token: string, dispatch: Dispatch): Promise<void> {
  await persistentStore.set('userToken', token);
  dispatch(setUserToken({ token }));
}

/**
 * The gate of the admin "Log in as this user" link. It sits inside the Redux store and around
 * every screen, so no route guard, no refresher and no tracker runs before the exchange ends.
 *
 * - With a captured code (`loginAsHandoff.ts`): it exchanges the code first. Success replaces the
 *   session and opens the app as the user. A refusal shows a message and leaves the session alone.
 * - Without a code: it renders the app at once.
 *
 * It also draws the admin session banner, and the message of an admin session that expired.
 */
const LoginAsGateComponent: React.FC<React.PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const [code] = useState(peekLoginAsCode);
  const [phase, setPhase] = useState<Phase>(code === null ? 'ready' : 'exchanging');
  const ended = useLoginAsEnded();
  useLoginAsAnalyticsPause();

  useEffect(() => {
    if (code === null) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const outcome = await exchangeLoginAsCode(code);
      clearLoginAsCode();
      if (outcome.kind === 'signedIn') {
        await applyLoginAsToken(outcome.token, dispatch);
      }
      if (!cancelled) {
        setPhase(outcome.kind === 'signedIn' ? 'ready' : 'refused');
      }
    })().catch(() => {
      // The error is not logged: it can quote the request. The link did not work.
      if (!cancelled) {
        setPhase('refused');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [code, dispatch]);

  if (phase === 'exchanging') {
    return <LoadingView />;
  }
  if (phase === 'refused') {
    return <LoginAsNotice kind="refused" />;
  }
  if (ended) {
    return <LoginAsNotice kind="ended" />;
  }
  return (
    <>
      <LoginAsBanner />
      {children}
    </>
  );
};

export const LoginAsGate = TypedMemo(LoginAsGateComponent);
